import type { Student } from '~/@entities/Student'
import type {
  LearningHistoryXlsxGrade,
  LearningHistoryXlsxParams,
} from '~/@entities/StudentHistory'
import type { Worksheet } from '~/@entities/Worksheet'
import type {
  AssignStudentWorksheetRequest,
  AssignWorksheetByStudentParams,
  CommonResponseAssignEchoResponse,
  CommonResponseListStudentWorksheetItemResponse,
  CommonResponseListStudentWorksheetVirtualScoringResponse,
  CommonResponseSliceGroupStudentWorksheetItemResponse,
  CommonResponseSliceGroupStudentWorksheetScoringListResponse,
  CommonResponseSliceStudentWorksheetItemResponse,
  CommonResponseSliceStudentWorksheetProblemItemResponse,
  CommonResponseStudentLearningWorksheetRelationShipResponse,
  CommonResponseStudentWorksheetItemResponse,
  CommonResponseWorksheetDetailByStudentResponse,
  CommonResponseWorksheetValidationResponse,
  CreateChallengeWorksheetParams,
  CreateConceptualWorksheetParams,
  CreateWrongSelfLearningWorksheetByStudentWorksheetIdParams,
  DeleteStudentWorksheetPayload,
  DownloadStudentLearningStatisticsByLessonClassParams,
  EssayAutoScoringPayload,
  GetLastChallengeWorksheetByReferenceTypeAndReferenceIdParams,
  GetPrevAndNextByOneOfNextStudentWorksheetIdParams,
  GetStudentWorksheetListByGradeParams,
  GetStudentWorksheetListByLessonClassParams,
  GetStudentWorksheetListParams,
  GetStudentWorksheetResultListByGradeParams,
  GetStudentWorksheetResultListByLessonClassParams,
  GetStudentWorksheetScoringListParams,
  GetStudentWorksheetWithHomeworkParams,
  GetVirtualScoringByStudentWorksheetIdsParams,
  InsertOrUpdateVirtualScoringPayload,
  ModifyStudentWorksheetRequest,
  Pageable,
  ResetVirtualScoringParams,
  ScoreDailyWorksheetForAutoScoredPayload,
  ScoreStudentWorksheetForAutoScoredPayload,
  ScoreStudentWorksheetPayload,
  UpdateChallengeWorksheetByWorksheetIdParams,
  UpdateConceptualWorksheetByWorksheetIdData,
  WorksheetValidationRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type HttpFile, type RequestConfig, type RequestMeta } from '~/http/client'
import type { Slice } from '~/schema/slice'

import {
  adaptAssignedWorksheet,
  adaptAssignedWorksheetDetail,
  adaptAssignedWorksheetDetails,
  adaptAssignedWorksheetProblemDetails,
  adaptAssignedWorksheetProblems,
  adaptAssignedWorksheetsWithHomeworks,
  adaptChallengeWorksheet,
  adaptChallengeWorksheets,
  adaptGroupAssignedWorksheets,
  adaptGroupWorksheetAssignEcho,
  adaptGroupWorksheetScorings,
  adaptSelfLearningWorksheetRelations,
  adaptStudentWorksheetVirtualScorings,
  adaptWorksheetAssignEcho,
  adaptWorksheetValidation,
  adaptWorksheetWithProblems,
} from './adapters'
import type {
  AssignedWorksheet,
  AssignedWorksheetDetail,
  AssignedWorksheetProblemDetail,
  AssignedWorksheetProblemWithEssay,
  AssignedWorksheetWithHomeworks,
  ChallengeReferenceType,
  ChallengeWorksheet,
  GroupAssignedWorksheet,
  GroupWorksheetAssignEcho,
  GroupWorksheetScoring,
  SelfLearningWorksheetRelations,
  StudentWorksheet,
  StudentWorksheetVirtualScoring,
  WorksheetAssignEcho,
  WorksheetValidation,
  WorksheetWithProblems,
} from './schemas'

export type AssignedWorksheetListParams = GetStudentWorksheetWithHomeworkParams
// 페이지를 넘기지 않고 첫 페이지만 읽는다. 앱이 넘기는 것은 정답 숨김 여부뿐이다
export type AssignedWorksheetProblemListParams = Pick<
  GetStudentWorksheetScoringListParams,
  'answerHideFlag'
>
export type SelfLearningWorksheetRelationsParams = GetPrevAndNextByOneOfNextStudentWorksheetIdParams
// 학생 앱은 유형칩과 소단원 기준으로만 챌린지를 만든다. 서버가 받는 커리큘럼 노드 전체 중 그 둘만 쓴다.
type WithChallengeReference<Params extends { referenceType: string }> = Omit<
  Params,
  'referenceType'
> & { referenceType: ChallengeReferenceType }
export type ChallengeWorksheetParams =
  WithChallengeReference<GetLastChallengeWorksheetByReferenceTypeAndReferenceIdParams>
export type ChallengeWorksheetCreation = WithChallengeReference<CreateChallengeWorksheetParams>
export type ChallengeWorksheetStepUpdate = UpdateChallengeWorksheetByWorksheetIdParams
export type SelfLearningWorksheetCreation =
  CreateWrongSelfLearningWorksheetByStudentWorksheetIdParams
export type WorksheetValidationInput = WorksheetValidationRequest
export type WorksheetScoringPayload = ScoreStudentWorksheetPayload
export type WorksheetAutoScoringPayload = ScoreStudentWorksheetForAutoScoredPayload
export type WorksheetEssayAutoScoringPayload = EssayAutoScoringPayload
export type WorksheetVirtualScoringPayload = InsertOrUpdateVirtualScoringPayload
export type VirtualScoringResetParams = ResetVirtualScoringParams

const AUTO_SCORING_VERSION = { version: 'V2' } as const

export const getAssignedWorksheet = async (
  studentWorksheetId: StudentWorksheet['id'],
  meta?: RequestMeta,
): Promise<AssignedWorksheet> => {
  const body = await httpClient().get<CommonResponseStudentWorksheetItemResponse>(
    `/student-worksheet/assign/${studentWorksheetId}`,
    { meta },
  )
  return adaptAssignedWorksheet(body.data)
}

export const getAssignedWorksheetProblems = async (
  studentWorksheetId: StudentWorksheet['id'],
  params?: AssignedWorksheetProblemListParams,
  meta?: RequestMeta,
): Promise<AssignedWorksheetProblemWithEssay[]> => {
  const body = await httpClient().get<CommonResponseSliceStudentWorksheetProblemItemResponse>(
    `/student-worksheet/assign/${studentWorksheetId}/problem`,
    { params, meta },
  )
  return adaptAssignedWorksheetProblems(body.data) ?? []
}

/** QR 학습지의 문제 목록 — 배정 전에 문제를 먼저 본다 */
export const getWorksheetWithProblems = async (
  worksheetId: Worksheet['id'],
  meta?: RequestMeta,
): Promise<WorksheetWithProblems> => {
  const body = await httpClient().get<CommonResponseWorksheetDetailByStudentResponse>(
    `/student-worksheet/worksheet/${worksheetId}`,
    { meta },
  )
  return adaptWorksheetWithProblems(body.data)
}

export const getAssignedWorksheetsWithHomeworks = async (
  { studentId, ...params }: AssignedWorksheetListParams,
  meta?: RequestMeta,
): Promise<AssignedWorksheetWithHomeworks[]> => {
  const body = await httpClient().get<CommonResponseListStudentWorksheetItemResponse>(
    `/student-worksheet/${studentId}/homeworks`,
    { params, meta },
  )
  return adaptAssignedWorksheetsWithHomeworks(body.data) ?? []
}

export const getSelfLearningWorksheetRelations = async (
  params: SelfLearningWorksheetRelationsParams,
  meta?: RequestMeta,
): Promise<SelfLearningWorksheetRelations> => {
  const body = await httpClient().get<CommonResponseStudentLearningWorksheetRelationShipResponse>(
    '/student-learning/self-learning',
    { params, meta },
  )
  return adaptSelfLearningWorksheetRelations(body.data)
}

/** 마지막 챌린지 학습지. null 은 "기존 챌린지 학습지 없음" 이라는 정상 응답이다 */
export const getLastChallengeWorksheet = async (
  params: ChallengeWorksheetParams,
  meta?: RequestMeta,
): Promise<ChallengeWorksheet | null> => {
  const body = await httpClient().get<CommonResponseStudentWorksheetItemResponse>(
    '/student-learning/self-learning/challenge/last',
    { params, meta },
  )
  return adaptChallengeWorksheet(body.data)
}

/***************** 개념 학습 *****************/
// 소단원마다 하나씩 만드는 자기주도 학습지. 챌린지 학습지와 같은 배정 기록 모양으로 온다.

/** 이 학생의 개념 학습 학습지들 */
export const getConceptualWorksheets = async (
  meta?: RequestMeta,
): Promise<ChallengeWorksheet[]> => {
  const body = await httpClient().get<CommonResponseListStudentWorksheetItemResponse>(
    '/student-learning/self-learning/conceptual',
    { meta },
  )
  return adaptChallengeWorksheets(body.data) ?? []
}

/** 소단원의 개념 학습지를 만들고 새 학생 학습지 id 를 받는다 */
export const createConceptualWorksheet = async (
  littleChapterId: CreateConceptualWorksheetParams['littleChapterId'],
  meta?: RequestMeta,
): Promise<StudentWorksheet['id'] | undefined> => {
  const params: CreateConceptualWorksheetParams = {
    referenceType: 'LITTLE_CHAPTER',
    referenceId: littleChapterId,
    littleChapterId,
  }
  const body = await httpClient().post<CommonResponseAssignEchoResponse>(
    '/student-learning/self-learning/conceptual',
    undefined,
    { params, meta },
  )
  return adaptWorksheetAssignEcho(body.data)?.assignList?.[0]?.studentWorksheetId
}

/** 개념 학습지를 다 풀었다고 표시한다 */
export const resolveConceptualWorksheet = async (
  studentWorksheetId: StudentWorksheet['id'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<UpdateConceptualWorksheetByWorksheetIdData>(
    '/student-learning/self-learning/conceptual',
    undefined,
    { params: { studentWorksheetId, learningStep: 'RESOLVED' }, meta },
  )
}

export const createChallengeWorksheet = async (
  params: ChallengeWorksheetCreation,
  meta?: RequestMeta,
): Promise<WorksheetAssignEcho> => {
  const body = await httpClient().post<CommonResponseAssignEchoResponse>(
    '/student-learning/self-learning/challenge',
    undefined,
    { params, meta },
  )
  return adaptWorksheetAssignEcho(body.data)
}

export const updateChallengeWorksheetStep = async (
  params: ChallengeWorksheetStepUpdate,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>('/student-learning/self-learning/challenge', undefined, {
    params,
    meta,
  })
}

export const createWrongSelfLearningWorksheet = async (
  params: SelfLearningWorksheetCreation,
  meta?: RequestMeta,
): Promise<WorksheetAssignEcho> => {
  const body = await httpClient().post<CommonResponseAssignEchoResponse>(
    '/student-learning/self-learning/wrong',
    undefined,
    { params, meta },
  )
  return adaptWorksheetAssignEcho(body.data)
}

export const createLevelUpSelfLearningWorksheet = async (
  params: SelfLearningWorksheetCreation,
  meta?: RequestMeta,
): Promise<WorksheetAssignEcho> => {
  const body = await httpClient().post<CommonResponseAssignEchoResponse>(
    '/student-learning/self-learning/level-up',
    undefined,
    { params, meta },
  )
  return adaptWorksheetAssignEcho(body.data)
}

export const validateWorksheet = async (
  input: WorksheetValidationInput,
  meta?: RequestMeta,
): Promise<WorksheetValidation> => {
  const body = await httpClient().post<CommonResponseWorksheetValidationResponse>(
    '/student-worksheet/validation',
    input,
    { meta },
  )
  return adaptWorksheetValidation(body.data)
}

/** 학생이 자기 자신에게 학습지를 출제한다(QR 학습지) */
export const assignWorksheetToSelf = async (
  worksheetId: AssignWorksheetByStudentParams['worksheetId'],
  meta?: RequestMeta,
): Promise<WorksheetAssignEcho> => {
  const body = await httpClient().post<CommonResponseAssignEchoResponse>(
    `/student-worksheet/${worksheetId}`,
    undefined,
    { meta },
  )
  return adaptWorksheetAssignEcho(body.data)
}

export const scoreAssignedWorksheet = async (
  studentWorksheetId: StudentWorksheet['id'],
  payload: WorksheetScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(
    `/student-worksheet/assign/${studentWorksheetId}/scoring`,
    payload,
    { meta },
  )
}

export const autoScoreAssignedWorksheet = async (
  studentWorksheetId: StudentWorksheet['id'],
  payload: WorksheetAutoScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(
    `/student-worksheet/assign/${studentWorksheetId}/auto-scoring`,
    payload,
    { params: AUTO_SCORING_VERSION, meta },
  )
}

/** 서술형 제출 — 전 문항 일괄·1회 확정(재제출 409). AI 가채점은 비동기로 시작된다 */
export const submitEssayAutoScoring = async (
  studentWorksheetId: StudentWorksheet['id'],
  payload: WorksheetEssayAutoScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(
    `/student-worksheet/assign/${studentWorksheetId}/essay-auto-scoring`,
    payload,
    { meta },
  )
}

/** 오늘의 학습(DAILY) 학습지는 채점 엔드포인트가 따로다 */
export const autoScoreDailyWorksheet = async (
  studentWorksheetId: StudentWorksheet['id'],
  payload: ScoreDailyWorksheetForAutoScoredPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(
    `/daily-worksheet/assign/${studentWorksheetId}/auto-scoring`,
    payload,
    { params: AUTO_SCORING_VERSION, meta },
  )
}

export const recordStudentCheat = async (
  studentId: Student['id'],
  studentWorksheetId: StudentWorksheet['id'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>(
    `/student-worksheet/student/${studentId}/${studentWorksheetId}/cheat`,
    undefined,
    { meta },
  )
}

/** 채점 전 답을 N문제씩 임시 제출한다(로컬스토리지 저장을 대체) */
export const upsertVirtualScoring = async (
  studentWorksheetId: StudentWorksheet['id'],
  payload: WorksheetVirtualScoringPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>(
    `/student-worksheet/${studentWorksheetId}/virtual-scoring`,
    payload,
    { params: AUTO_SCORING_VERSION, meta },
  )
}

export const resetVirtualScoring = async (
  { studentWorksheetId, ...params }: VirtualScoringResetParams,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<unknown>(`/student-worksheet/${studentWorksheetId}/virtual-scoring`, {
    params,
    meta,
  })
}

export const getStudentWorksheetVirtualScorings = async (
  params: GetVirtualScoringByStudentWorksheetIdsParams,
  meta?: RequestMeta,
): Promise<StudentWorksheetVirtualScoring[]> => {
  const body = await httpClient().get<CommonResponseListStudentWorksheetVirtualScoringResponse>(
    '/student-worksheet/virtual-scoring',
    { params, meta },
  )
  return adaptStudentWorksheetVirtualScorings(body.data) ?? []
}

/***************** 선생님 앱의 출제 기록 *****************/

// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.
// 목록은 학습지 종류(tags)·제목·기간으로 거른다. tag 는 tags 로 옮기며 남은 옛 이름이다.
export type AssignedWorksheetDetailListParams = Omit<
  GetStudentWorksheetListParams,
  'pageable' | 'studentId' | 'tag'
> &
  Pageable
export type GroupAssignedWorksheetListParams = Omit<
  GetStudentWorksheetListByGradeParams,
  'pageable' | 'schoolType' | 'grade' | 'tag'
> &
  Pageable
export type GroupWorksheetGradeTarget = Pick<
  GetStudentWorksheetResultListByGradeParams,
  'schoolType' | 'grade'
>
export type GroupWorksheetLessonClassTarget = Pick<
  GetStudentWorksheetListByLessonClassParams,
  'lessonClassId'
>
export type GroupWorksheetScoringListParams = Pageable
export type AssignedWorksheetProblemDetailListParams = Pageable &
  Pick<GetStudentWorksheetScoringListParams, 'answerHideFlag'>
export type WorksheetAssignBody = AssignStudentWorksheetRequest

/** 학생들에게 학습지를 출제한다. 화면이 출제 이벤트에 더 실을 속성은 trackingProps 로 넘긴다 */
export const assignWorksheets = async (
  body: WorksheetAssignBody,
  { meta, trackingProps }: Pick<RequestConfig, 'meta' | 'trackingProps'> = {},
): Promise<GroupWorksheetAssignEcho> => {
  const res = await httpClient().post<CommonResponseAssignEchoResponse>(
    '/student-worksheet',
    body,
    {
      meta,
      trackingProps,
    },
  )
  return adaptGroupWorksheetAssignEcho(res.data)
}
export type StudentWorksheetDatetimeBody = ModifyStudentWorksheetRequest

/** 선생님이 조회하는 배정 기록 한 건 */
export const getAssignedWorksheetDetail = async (
  studentWorksheetId: StudentWorksheet['id'],
  meta?: RequestMeta,
): Promise<AssignedWorksheetDetail> => {
  const body = await httpClient().get<CommonResponseStudentWorksheetItemResponse>(
    `/student-worksheet/assign/${studentWorksheetId}`,
    { meta },
  )
  return adaptAssignedWorksheetDetail(body.data)
}

export const getAssignedWorksheetDetails = async (
  studentId: Student['id'],
  params: AssignedWorksheetDetailListParams,
  meta?: RequestMeta,
): Promise<Slice<AssignedWorksheetDetail>> => {
  const body = await httpClient().get<CommonResponseSliceStudentWorksheetItemResponse>(
    `/student-worksheet/${studentId}`,
    { params, meta },
  )
  return adaptAssignedWorksheetDetails(body.data)
}

export const getAssignedWorksheetProblemDetails = async (
  studentWorksheetId: StudentWorksheet['id'],
  params: AssignedWorksheetProblemDetailListParams,
  config?: { meta?: RequestMeta; signal?: AbortSignal },
): Promise<AssignedWorksheetProblemDetail[]> => {
  const body = await httpClient().get<CommonResponseSliceStudentWorksheetProblemItemResponse>(
    `/student-worksheet/assign/${studentWorksheetId}/problem`,
    { params, ...config },
  )
  return adaptAssignedWorksheetProblemDetails(body.data)
}

export const getGradeAssignedWorksheets = async (
  { schoolType, grade }: GroupWorksheetGradeTarget,
  params: GroupAssignedWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<GroupAssignedWorksheet>> => {
  const body = await httpClient().get<CommonResponseSliceGroupStudentWorksheetItemResponse>(
    `/student-worksheet/grade/${schoolType}/${grade}`,
    { params, meta },
  )
  return adaptGroupAssignedWorksheets(body.data)
}

export const getLessonClassAssignedWorksheets = async (
  { lessonClassId }: GroupWorksheetLessonClassTarget,
  params: GroupAssignedWorksheetListParams,
  meta?: RequestMeta,
): Promise<Slice<GroupAssignedWorksheet>> => {
  const body = await httpClient().get<CommonResponseSliceGroupStudentWorksheetItemResponse>(
    `/student-worksheet/lesson-class/${lessonClassId}`,
    { params, meta },
  )
  return adaptGroupAssignedWorksheets(body.data)
}

export const getGradeWorksheetScorings = async (
  { schoolType, grade, worksheetId }: Omit<GetStudentWorksheetResultListByGradeParams, 'pageable'>,
  params: GroupWorksheetScoringListParams,
  config?: { meta?: RequestMeta; signal?: AbortSignal },
): Promise<Slice<GroupWorksheetScoring>> => {
  const body = await httpClient().get<CommonResponseSliceGroupStudentWorksheetScoringListResponse>(
    `/student-worksheet/grade/${schoolType}/${grade}/${worksheetId}/scoring`,
    { params, ...config },
  )
  return adaptGroupWorksheetScorings(body.data)
}

export const getLessonClassWorksheetScorings = async (
  {
    lessonClassId,
    worksheetId,
  }: Omit<GetStudentWorksheetResultListByLessonClassParams, 'pageable'>,
  params: GroupWorksheetScoringListParams,
  config?: { meta?: RequestMeta; signal?: AbortSignal },
): Promise<Slice<GroupWorksheetScoring>> => {
  const body = await httpClient().get<CommonResponseSliceGroupStudentWorksheetScoringListResponse>(
    `/student-worksheet/lesson-class/${lessonClassId}/${worksheetId}/scoring`,
    { params, ...config },
  )
  return adaptGroupWorksheetScorings(body.data)
}

/** 출제를 취소한다. 학생이 푼 기록도 함께 지워진다 */
export const deleteAssignedWorksheets = async (
  studentWorksheetIds: DeleteStudentWorksheetPayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<unknown>('/student-worksheet', { data: studentWorksheetIds, meta })
}

/** 출제일·채점일을 고친다. 보내지 않은 날짜는 그대로 둔다 */
export const updateAssignedWorksheetDatetime = async (
  studentWorksheetId: StudentWorksheet['id'],
  body: StudentWorksheetDatetimeBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<unknown>(`/student-worksheet/assign/${studentWorksheetId}`, body, {
    meta,
  })
}

/***************** 학습지 통계 엑셀 *****************/
// 조건은 학습 통계 엑셀과 같다(StudentHistory 가 발행한다)
export const getGradeWorksheetStatisticsSheet = async (
  { schoolType, grade }: LearningHistoryXlsxGrade,
  params: LearningHistoryXlsxParams,
  meta?: RequestMeta,
): Promise<HttpFile> =>
  httpClient().getFile(`/student-worksheet/grade/${schoolType}/${grade}/xlsx`, { params, meta })

export const getLessonClassWorksheetStatisticsSheet = async (
  lessonClassId: DownloadStudentLearningStatisticsByLessonClassParams['lessonClassId'],
  params: LearningHistoryXlsxParams,
  meta?: RequestMeta,
): Promise<HttpFile> =>
  httpClient().getFile(`/student-worksheet/lesson-class/${lessonClassId}/xlsx`, { params, meta })
